import { priceCurrencyValues } from '../../domain/constants';
import {
	myLQAContractBillingIntervalValues,
	myLQAContractStateValues,
} from '../../domain/my-lqa/contract';
import { paymentProviderValues } from '../../domain/payment';
import { z } from 'zod';

const ownerId = z
	.union([z.string(), z.number()])
	.optional()
	.nullable()
	.transform((val) => {
		// if null or undefined, keep it
		if (val == null) return val;
		// otherwise coerce to number
		return typeof val === 'number' ? val : Number(val);
	});

export const myLQAOwnershipSchema = z
	.object({
		ownedByBrandId: ownerId,
		ownedByHotelId: ownerId,
		forBrand: ownerId,
	})
	.superRefine((data, ctx) => {
		const hasBrand = !!data.ownedByBrandId;
		const hasHotel = !!data.ownedByHotelId;
		if ((hasBrand && !hasHotel) || (!hasBrand && hasHotel)) {
			return;
		}

		ctx.addIssue({
			code: 'custom',
			path: ['owner'],
			message: 'Select hotel or brand.',
		});
	});

export const myLQAContractBaseSchema = z.object({
	tierVariantId: z.uuid('Tier variant is required').optional().nullable(),
	customTierSeats: z.coerce
		.number()
		.min(1, 'Min value is at least 1')
		.max(2147483647, 'Max value is 2147483647')
		.positive('Value must be a positive number')
		.optional()
		.nullable(),
	paymentInterval: z.enum(myLQAContractBillingIntervalValues),
	paymentProvider: z.enum(paymentProviderValues).optional().nullable(),
	paymentProviderMetaData: z.object({}).catchall(z.unknown()).optional(),
	currency: z.enum(priceCurrencyValues),
	billingAgentId: z.uuid().optional(), // only when admin creates contract for external payment provider
	autoRenewal: z.boolean().optional(),
	licensedHotelsIds: z.array(z.number()).optional(),
	notes: z
		.string()
		.max(300, 'Notes must be at most 300 characters long')
		.optional(),
});

export const myLQAContractCreateSchema = myLQAContractBaseSchema
	.and(myLQAOwnershipSchema)
	.and(
		z.object({
			startDate: z.preprocess(
				(val) => (val === '' ? undefined : val),
				z.iso.date().optional()
			),
			endDate: z.preprocess(
				(val) => (val === '' ? undefined : val),
				z.iso.date().optional()
			),
			email: z.preprocess(
				(val) => (val === '' ? undefined : val),
				z
					.email('Enter a valid email address')
					.max(320, 'Email must be at most 320 characters long')
					.optional()
					.nullable()
			),
		})
	)
	.superRefine((data, ctx) => {
		const hasCustomSeats =
			data.customTierSeats !== null && (data.customTierSeats ?? 0) > 0;
		if (hasCustomSeats || !!data.tierVariantId) {
			return;
		}

		if (data.startDate && data.endDate && data.endDate <= data.startDate) {
			ctx.addIssue({
				code: 'custom',
				path: ['endDate'],
				message: 'End date must be greater than start date.',
			});
		}

		ctx.addIssue({
			code: 'custom',
			path: ['tierVariantId'],
			message: 'Select tier variant.',
		});
		ctx.addIssue({
			code: 'custom',
			path: ['customTierSeats'],
			message: 'Enter number of seats.',
		});
	});

export const myLQAContractUpdateSchema = myLQAContractBaseSchema
	.extend({
		tierVariantId: z.uuid('Tier variant is required').optional().nullable(),
		customTierSeats: z
			.number()
			.min(1, 'At least 1 seed is required ')
			.positive('Number of seats must be positive number')
			.optional()
			.nullable(),
		instantTierChange: z.boolean().optional(),
		ownedByBrandId: z.number().optional().nullable(),
		ownedByHotelId: z.number().optional().nullable(),
	})
	.partial();

export const myLQAContractEndSchema = z.object({
	endDate: z.string().optional().nullable(),
});

export const myLQAContractsQuerySchema = z
	.object({
		ownedByBrandId: z.array(ownerId),
		ownedByHotelId: z.array(ownerId),
		fulltext: z.string(),
		owner: z
			.enum(['all', 'brand', 'hotel'])
			.refine((v) => (v === 'all' ? undefined : v)),
		state: z
			.union([
				z.enum(myLQAContractStateValues),
				z.array(z.enum(myLQAContractStateValues)),
				z.string(),
			])
			.refine((v) => {
				if (!v) return undefined;
				if (!myLQAContractStateValues.find((s) => s === v)) return undefined;

				return Array.isArray(v) ? v : [v];
			}),
		billingInterval: z.string(),
		paymentProvider: z.string(),
		tierType: z.string(),
		autoRenewal: z.boolean(),
	})
	.partial();
